import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import SeoHead from '../../components/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.cjsolutionsltd.com';

const privacyJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy | CJ Solutions Ltd',
    url: `${siteUrl}/privacy-policy`,
    description: 'Read the Privacy Policy of CJ Solutions Ltd. Learn how we collect, use, and protect your personal data in accordance with GDPR and Cyprus data protection law.',
    publisher: {
      '@type': 'Organization',
      name: 'CJ Solutions Ltd',
      url: siteUrl,
    },
    inLanguage: 'en-GB',
    dateModified: '2025-01-01',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: `${siteUrl}/privacy-policy` },
    ],
  },
];

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Privacy Policy | CJ Solutions Ltd — Data Protection in Cyprus"
        description="Read the Privacy Policy of CJ Solutions Ltd. Learn how we collect, use, and protect your personal data in accordance with GDPR and Cyprus data protection law."
        keywords="privacy policy, data protection, GDPR Cyprus, CJ Solutions privacy"
        canonical="/privacy-policy"
        jsonLd={privacyJsonLd}
      />
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-amber-500/20 text-amber-400 text-sm font-semibold uppercase tracking-widest rounded-full mb-6">
            Legal
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Privacy Policy
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Last updated: January 2025 &nbsp;·&nbsp; CJ Solutions Ltd.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="prose prose-slate max-w-none space-y-12">

          {/* 1 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              1. Introduction
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              CJ Solutions Ltd ("we", "us", or "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data when you visit our website{' '}
              <a href="https://www.cjsolutionsltd.com" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">
                www.cjsolutionsltd.com
              </a>{' '}
              or engage with our services.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mt-4">
              CJ Solutions Ltd is a company registered in Cyprus. We act as a data controller in respect of the personal data we process about you. By using our website or services, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              2. Information We Collect
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              We may collect and process the following categories of personal data:
            </p>
            <ul className="space-y-3">
              {[
                { title: 'Identity Data', desc: 'Full name, job title, company name.' },
                { title: 'Contact Data', desc: 'Email address, telephone number, postal address.' },
                { title: 'Technical Data', desc: 'IP address, browser type and version, time zone setting, browser plug-in types and versions, operating system and platform, and other technology on the devices you use to access our website.' },
                { title: 'Usage Data', desc: 'Information about how you use our website and services, including pages visited, links clicked, and time spent on pages.' },
                { title: 'Communications Data', desc: 'Any information you provide when you contact us via email, phone, or our website contact form, including the content of your messages.' },
                { title: 'Marketing & Preferences Data', desc: 'Your preferences in receiving marketing communications from us and your communication preferences.' },
              ].map((item) => (
                <li key={item.title} className="flex gap-3 items-start">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                  <span className="text-slate-600 text-lg leading-relaxed">
                    <strong className="text-slate-800">{item.title}:</strong> {item.desc}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              3. How We Collect Your Data
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">We collect data through the following means:</p>
            <ul className="space-y-3">
              {[
                'Direct interactions — when you fill in forms on our website, contact us by email or phone, or request information about our services.',
                'Automated technologies — as you interact with our website, we may automatically collect Technical Data and Usage Data using cookies, server logs, and similar technologies.',
                'Third parties — we may receive data about you from analytics providers, advertising networks, and search information providers.',
              ].map((item, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                  <span className="text-slate-600 text-lg leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              4. How We Use Your Data
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
            </p>
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">Purpose</th>
                    <th className="px-6 py-4 text-sm font-semibold text-slate-700 uppercase tracking-wider">Lawful Basis</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[
                    ['To respond to your enquiries and provide our services', 'Legitimate interests / Contract performance'],
                    ['To send you marketing communications (with your consent)', 'Consent'],
                    ['To improve our website and services', 'Legitimate interests'],
                    ['To comply with legal and regulatory obligations', 'Legal obligation'],
                    ['To manage our relationship with you', 'Contract performance / Legitimate interests'],
                    ['To prevent fraud and ensure security', 'Legitimate interests / Legal obligation'],
                  ].map(([purpose, basis], i) => (
                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 text-slate-600 text-base">{purpose}</td>
                      <td className="px-6 py-4 text-slate-600 text-base">{basis}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              5. Disclosure of Your Data
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              We may share your personal data with the following parties:
            </p>
            <ul className="space-y-3">
              {[
                'Service providers acting as processors who provide IT and system administration services.',
                'Professional advisers including lawyers, bankers, auditors, and insurers who provide consultancy, banking, legal, insurance, and accounting services.',
                'Regulators and other authorities who require reporting of processing activities in certain circumstances.',
                'Third parties to whom we may choose to sell, transfer, or merge parts of our business or our assets.',
              ].map((item, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                  <span className="text-slate-600 text-lg leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-slate-600 text-lg leading-relaxed mt-4">
              We require all third parties to respect the security of your personal data and to treat it in accordance with the law. We do not allow our third-party service providers to use your personal data for their own purposes.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              6. International Transfers
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Some of our external third parties are based outside the European Economic Area (EEA), so their processing of your personal data will involve a transfer of data outside the EEA. Whenever we transfer your personal data out of the EEA, we ensure a similar degree of protection is afforded to it by ensuring at least one of the following safeguards is implemented: adequacy decisions, standard contractual clauses approved by the European Commission, or other appropriate safeguards as permitted under applicable data protection law.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              7. Data Retention
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              We will only retain your personal data for as long as necessary to fulfil the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements. To determine the appropriate retention period for personal data, we consider the amount, nature, and sensitivity of the personal data, the potential risk of harm from unauthorised use or disclosure, the purposes for which we process your personal data, and whether we can achieve those purposes through other means, and the applicable legal requirements.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mt-4">
              In some circumstances you can ask us to delete your data — please see the section on your legal rights below for further information.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              8. Cookies
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              Our website uses cookies to distinguish you from other users of our website. This helps us to provide you with a good experience when you browse our website and also allows us to improve our site. We use the following types of cookies:
            </p>
            <ul className="space-y-3">
              {[
                { name: 'Strictly necessary cookies', desc: 'Required for the operation of our website.' },
                { name: 'Analytical/performance cookies', desc: 'Allow us to recognise and count the number of visitors and to see how visitors move around our website.' },
                { name: 'Functionality cookies', desc: 'Used to recognise you when you return to our website and to personalise our content to you.' },
                { name: 'Targeting cookies', desc: 'Record your visit to our website, the pages you have visited, and the links you have followed to deliver adverts more relevant to your interests.' },
              ].map((item) => (
                <li key={item.name} className="flex gap-3 items-start">
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-amber-500 flex-shrink-0"></span>
                  <span className="text-slate-600 text-lg leading-relaxed">
                    <strong className="text-slate-800">{item.name}:</strong> {item.desc}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-slate-600 text-lg leading-relaxed mt-4">
              You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies. If you disable or refuse cookies, please note that some parts of this website may become inaccessible or not function properly.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              9. Data Security
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used, or accessed in an unauthorised way, altered, or disclosed. In addition, we limit access to your personal data to those employees, agents, contractors, and other third parties who have a business need to know. They will only process your personal data on our instructions and they are subject to a duty of confidentiality.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mt-4">
              We have put in place procedures to deal with any suspected personal data breach and will notify you and any applicable regulator of a breach where we are legally required to do so.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              10. Your Legal Rights
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-4">
              Under certain circumstances, you have rights under data protection laws in relation to your personal data:
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-eye-line', title: 'Right to Access', desc: 'Request a copy of the personal data we hold about you.' },
                { icon: 'ri-edit-line', title: 'Right to Rectification', desc: 'Request correction of inaccurate or incomplete personal data.' },
                { icon: 'ri-delete-bin-line', title: 'Right to Erasure', desc: 'Request deletion of your personal data in certain circumstances.' },
                { icon: 'ri-forbid-line', title: 'Right to Object', desc: 'Object to processing of your personal data where we rely on legitimate interests.' },
                { icon: 'ri-pause-circle-line', title: 'Right to Restriction', desc: 'Request restriction of processing of your personal data.' },
                { icon: 'ri-share-forward-line', title: 'Right to Portability', desc: 'Request transfer of your personal data to you or a third party.' },
              ].map((right) => (
                <div key={right.title} className="flex gap-4 p-5 rounded-xl border border-slate-200 hover:border-amber-300 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
                    <i className={`${right.icon} text-amber-500 text-xl`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{right.title}</h4>
                    <p className="text-slate-600 text-sm leading-relaxed">{right.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-lg leading-relaxed mt-6">
              If you wish to exercise any of the rights set out above, please contact us at{' '}
              <a href="mailto:info@cjsolutionsltd.com" className="text-amber-500 hover:underline font-medium">
                info@cjsolutionsltd.com
              </a>.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mt-4">
              You also have the right to make a complaint at any time to the Commissioner for Personal Data Protection in Cyprus (the supervisory authority for data protection issues in Cyprus). We would, however, appreciate the chance to deal with your concerns before you approach the supervisory authority, so please contact us in the first instance.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              11. Third-Party Links
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Our website may include links to third-party websites, plug-ins, and applications. Clicking on those links or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements. When you leave our website, we encourage you to read the privacy policy of every website you visit.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              12. Changes to This Privacy Policy
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              We reserve the right to update this Privacy Policy at any time. We will notify you of any significant changes by posting the new Privacy Policy on this page with an updated "Last updated" date. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 pb-3 border-b border-slate-200">
              13. Contact Us
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              If you have any questions about this Privacy Policy or our privacy practices, please contact our data privacy team:
            </p>
            <div className="bg-slate-50 rounded-2xl p-8 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100">
                  <i className="ri-building-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Company</p>
                  <p className="text-slate-900 font-semibold text-lg">CJ Solutions Ltd</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100">
                  <i className="ri-map-pin-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Address</p>
                  <p className="text-slate-900 font-semibold text-lg">Limassol, Cyprus</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100">
                  <i className="ri-mail-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Email</p>
                  <a href="mailto:info@cjsolutionsltd.com" className="text-amber-500 hover:underline font-semibold text-lg">
                    info@cjsolutionsltd.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-100">
                  <i className="ri-phone-line text-amber-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm text-slate-500 font-medium uppercase tracking-wider">Phone</p>
                  <a href="tel:+35795981879" className="text-amber-500 hover:underline font-semibold text-lg">
                    +357 95981879
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>

        {/* Back link */}
        <div className="mt-16 pt-8 border-t border-slate-200 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-amber-500 transition-colors font-medium text-lg cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <a
            href="mailto:info@cjsolutionsltd.com"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line"></i>
            Contact Us
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
}
